// Feature-flag registry for the admin web app.
//
// A flag = true means the screen is BUILT, wired to a live backend endpoint, and
// shown in nav + reachable by route. A flag = false means the feature is reserved
// / locked: it is hidden from nav AND blocked at the route guard (so it is not
// reachable by typing the URL). The backend also returns 403/404 for reserved
// features, so this is defense-in-depth — but the UI now matches what is actually
// shippable instead of silently exposing locked routes.
//
// PARITY (2026-06-22): web flags are aligned with the Flutter Admin app's
// `AppFeatureFlags` locks so that Super/Tech testers see the same locked surfaces
// on both clients. The Flutter source of truth is
// `Admin_App/frontend/lib/core/constants/feature_flags.dart`.
//
// Unlock flow for a future feature: build the screen + verify the backend guard,
// then flip its flag to true here. Hiding it again is a one-line revert.
export const featureFlags = {
  // --- Built + live (web-only Super/Tech surfaces, not locked in Flutter) ---
  notificationTemplates: true,
  manageAdmins: true,

  // --- Locked to match Flutter AppFeatureFlags (2026-06-22) ---
  //     hidden from nav + URL-blocked, mirroring the Flutter locks. ---
  apiKeys: false, // Flutter apiKeysEnabled = false
  qrConfig: false, // Flutter qrConfigEnabled = false
  securityIncidents: false, // Flutter securityEnabled = false
  auditAlertRules: false, // Flutter alertRulesEnabled = false
  dataExports: false, // Flutter dataExportsEnabled = false
  notificationSuppressions: false, // Flutter platformParamsSuppressionsEnabled = false
  subTenantMapping: false, // Flutter subTenantMappingEnabled = false

  // --- Locked per earlier product decision (also locked in Flutter) ---
  delegation: false, // Flutter delegationEnabled = false
  sessions: false, // Flutter sessionsEnabled = false
  featureFlagsAdmin: false,
  platformParams: false,

  // --- Reserved / not yet built (no screen, no route; locked in Flutter too) ---
  preOrders: false, // Flutter preOrderEnabled = false
  kdsBatches: false, // Flutter kdsBatchesEnabled = false
  bulkOrders: false, // Flutter bulkOrderEnabled = false
} as const;

export type FeatureFlag = keyof typeof featureFlags;
export const isEnabled = (flag: FeatureFlag): boolean => featureFlags[flag];
